/**
 * The per-order credits page (R17): `/credits/:designToken`, linked from the
 * order email and packing slip, so anyone holding the tokens (a gift recipient
 * included) can find who made the icons and fonts. It shows the credits frozen
 * with the design at add to cart; it never loads the catalogue or the design.
 */

import { useEffect, useState } from 'react';
import { EngineError, getDesignCredits } from '../api/client.js';
import type { DesignCredits, FrozenIconCredit } from '../api/types.js';
import { safeWebUrl } from './Credits.js';

const LICENSE_NAMES: Record<string, string> = {
    cc_by_3_0: 'CC BY 3.0',
    cc0: 'CC0 (public domain)',
};

type Phase =
    | { kind: 'loading' }
    | { kind: 'ready'; credits: DesignCredits }
    | { kind: 'error'; title: string; detail: string };

/** The design token in a `/credits/:designToken` path, or null when the path is something else. */
export function creditsTokenFromPath(pathname: string): string | null {
    const m = /^\/credits\/([^/]+)\/?$/.exec(pathname);
    return m ? decodeURIComponent(m[1]) : null;
}

export function CreditsPage({ designToken }: { designToken: string }): React.JSX.Element {
    const [phase, setPhase] = useState<Phase>({ kind: 'loading' });

    useEffect(() => {
        // The URL carries a design token; keep these pages out of search results.
        const robots = document.createElement('meta');
        robots.name = 'robots';
        robots.content = 'noindex';
        document.head.appendChild(robots);
        document.title = 'Credits — LITKO custom tokens';

        let cancelled = false;
        getDesignCredits(designToken).then(
            (credits) => {
                if (!cancelled) {
                    setPhase({ kind: 'ready', credits });
                }
            },
            (e: unknown) => {
                if (!cancelled) {
                    setPhase({ kind: 'error', ...describe(e) });
                }
            },
        );
        return () => {
            cancelled = true;
            robots.remove();
        };
    }, [designToken]);

    return (
        <main className="credits-page">
            <h1>Credits for your tokens</h1>
            {phase.kind === 'loading' && <p>Loading…</p>}
            {phase.kind === 'error' && (
                <>
                    <h2>{phase.title}</h2>
                    <p>{phase.detail}</p>
                </>
            )}
            {phase.kind === 'ready' && <CreditsBody credits={phase.credits} />}
        </main>
    );
}

function CreditsBody({ credits }: { credits: DesignCredits }): React.JSX.Element {
    if (!credits.recorded) {
        return <p>Credits are unavailable for this design: it was made before we started recording them.</p>;
    }
    if (credits.icons.length === 0 && credits.fonts.length === 0) {
        return <p>This design uses no third-party icons or fonts.</p>;
    }
    return (
        <>
            <p className="credits-page__lead">The artwork on these tokens includes the work of these artists, used under their licences.</p>
            {credits.icons.length > 0 && (
                <>
                    <h2>Icons</h2>
                    <ul className="credits__list">
                        {credits.icons.map((icon, i) => (
                            <li key={i}><IconCredit icon={icon} /></li>
                        ))}
                    </ul>
                </>
            )}
            {credits.fonts.length > 0 && (
                <>
                    <h2>Fonts</h2>
                    <ul className="credits__list">
                        {credits.fonts.map((font, i) => (
                            <li key={i}>
                                {font.displayName !== null && <strong>{font.displayName}</strong>}
                                {font.displayName !== null && font.attributionText !== null && ' — '}
                                {font.attributionText}
                            </li>
                        ))}
                    </ul>
                </>
            )}
        </>
    );
}

function IconCredit({ icon }: { icon: FrozenIconCredit }): React.JSX.Element {
    const license = icon.licenseType !== null ? (LICENSE_NAMES[icon.licenseType] ?? icon.licenseType) : null;
    return (
        <>
            {icon.displayName !== null && <strong>{icon.displayName}</strong>}
            {icon.author !== null ? (
                <> by <Link href={icon.authorUrl}>{icon.author}</Link></>
            ) : icon.attributionText !== null ? (
                <> — {icon.attributionText}</>
            ) : null}
            {license !== null && <> · <Link href={icon.licenseUrl}>{license}</Link></>}
            {icon.modification !== null && <> · {icon.modification}</>}
        </>
    );
}

function Link({ href, children }: { href: string | null; children: React.ReactNode }): React.JSX.Element {
    const safe = safeWebUrl(href);
    return safe === null ? <>{children}</> : <a href={safe} target="_blank" rel="noopener noreferrer">{children}</a>;
}

function describe(e: unknown): { title: string; detail: string } {
    if (e instanceof EngineError && e.code === 'not_found') {
        return { title: 'We couldn’t find these credits', detail: 'Please check the link on your order email or packing slip.' };
    }
    if (e instanceof EngineError && e.code === 'rate_limited') {
        return { title: 'Too many requests', detail: 'Please wait a moment and reload.' };
    }
    return { title: 'Couldn’t load the credits', detail: 'Please try again later.' };
}
